import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import type { ScrollPage } from "@/lib/api/queries/scrolls";
import type { Scroll } from "@/lib/supabase";

export type ScrollListInfiniteData = InfiniteData<ScrollPage, string | undefined>;

export function patchScrollInfiniteData(
  data: ScrollListInfiniteData | undefined,
  updater: (items: Scroll[]) => Scroll[],
): ScrollListInfiniteData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: updater(page.items),
    })),
  };
}

export function patchScrollInAllQueries(
  qc: QueryClient,
  scrollId: string,
  patch: Partial<Scroll>,
) {
  const updatedAt = patch.updated_at ?? new Date().toISOString();
  qc.setQueriesData<ScrollListInfiniteData>(
    { queryKey: ["scrolls"] },
    (existing) =>
      patchScrollInfiniteData(existing, (items) =>
        items.map((item) =>
          item.id === scrollId
            ? { ...item, ...patch, updated_at: updatedAt }
            : item,
        ),
      ),
  );
}

export function collectAllScrollQueryData(qc: QueryClient) {
  return qc.getQueriesData<ScrollListInfiniteData>({ queryKey: ["scrolls"] });
}

export function restoreAllScrollQueryData(
  qc: QueryClient,
  snapshots: Array<[readonly unknown[], ScrollListInfiniteData | undefined]>,
) {
  for (const [key, data] of snapshots) {
    qc.setQueryData(key, data);
  }
}

export function findScrollInSnapshots(
  snapshots: Array<[readonly unknown[], ScrollListInfiniteData | undefined]>,
  scrollId: string,
): Scroll | undefined {
  for (const [, data] of snapshots) {
    if (!data) continue;
    for (const page of data.pages) {
      const hit = page.items.find((item) => item.id === scrollId);
      if (hit) return hit;
    }
  }
  return undefined;
}

/** Deduped scroll rows from all cached list queries. */
export function collectAllScrollsFromQueryCache(qc: QueryClient): Scroll[] {
  const entries = collectAllScrollQueryData(qc);
  const byId = new Map<string, Scroll>();
  for (const [, data] of entries) {
    if (!data) continue;
    for (const page of data.pages) {
      for (const item of page.items) {
        byId.set(item.id, item);
      }
    }
  }
  return Array.from(byId.values());
}

export function resolveScrollCatalog(
  qc: QueryClient,
  appScrolls: readonly Scroll[],
): Scroll[] {
  const fromCache = collectAllScrollsFromQueryCache(qc);
  if (fromCache.length === 0) return [...appScrolls];
  const byId = new Map<string, Scroll>();
  for (const scroll of appScrolls) byId.set(scroll.id, scroll);
  for (const scroll of fromCache) byId.set(scroll.id, scroll);
  return Array.from(byId.values());
}
